import * as React from "react";

import { MetricsOverview } from "@editor/components/analytics/home/MetricsOverview";
import { TopPagesTable } from "@editor/components/analytics/home/TopPagesTable";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";

import useAnalyticsApiV2 from "@/features/analytics/hooks/useAnalyticsApiV2";
import { InsightsCard } from "@/features/analytics/insights/InsightsCard";
import AnalyticsLayout from "@/features/analytics/Layout";
import useAnalyticsInsights from "@/features/analytics/useAnalyticsInsights";
import useBasicAnalyticsRead from "@/features/analytics/useBasicAnalyticsRead";
import Button from "@replo/design-system/components/button/Button";
import { AppWindow, Sparkles } from "lucide-react";
import {
  convertRangeToRangeV2,
  getRangesFromTimePeriod,
} from "replo-utils/analytics";

const Card: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, children }) => {
  return (
    <div className="flex flex-col gap-2 rounded-lg">
      <div className="flex flex-row items-center justify-between">
        <div className="flex items-center gap-2">
          {icon}
          <span className="text-base font-semibold">{title}</span>
        </div>
        <Button variant="secondary">See all</Button>
      </div>
      {children}
    </div>
  );
};

export const AnalyticsHome: React.FC = () => {
  const workspaceId = useCurrentWorkspaceId() ?? "";

  const { allInsights, activeGroupInsights } =
    useAnalyticsInsights("build-in-replo");

  const insightToShow = activeGroupInsights?.[0] ?? allInsights?.[0];

  const { mainRangeResults, isLoading: isTopPagesLoading } =
    useBasicAnalyticsRead({
      workspaceId,
      type: "withUrlSyncQuery",
      queryConfig: {
        useChartInterval: false,
        queryOverrides: {
          sortMetric: "unique_sessions",
          order: "DESC",
          limit: 3,
        },
      },
    });

  const ranges = getRangesFromTimePeriod({
    selectedTimePeriod: {
      type: "relative",
      value: "last-7-days",
    },
    chartInterval: null,
    timeZone: "UTC",
  }).updatedRanges;

  const firstCompareRange = ranges.compareAtRanges[0];

  const {
    mainFindResults,
    compareFindResults,
    isLoading: isFindResultsLoading,
  } = useAnalyticsApiV2({
    workspaceId,
    type: "withoutUrlSyncQuery",
    queryConfig: {
      ranges: {
        main: convertRangeToRangeV2(ranges.mainRange),
        compare: firstCompareRange
          ? convertRangeToRangeV2(firstCompareRange)
          : undefined,
      },
      sort: {
        by: "views",
        order: "DESC",
      },
      limit: 3,
      offset: 0,
      filters: [
        {
          type: "domain",
          operator: "EQ",
          values: ["aloha.com"],
        },
      ],
      touchType: "first",
      group: "domain",
    },
  });

  return (
    <AnalyticsLayout headerTitle="Home" hideMetricsOptions>
      <div className="flex flex-col gap-6">
        <MetricsOverview
          mainFindResults={mainFindResults}
          compareFindResults={compareFindResults}
          isLoading={isFindResultsLoading}
        />
        <div className="grid grid-cols-2 gap-4">
          <Card title="Insights" icon={<Sparkles size={18} />}>
            {insightToShow && <InsightsCard insight={insightToShow} />}
          </Card>
          <Card title="Top Pages" icon={<AppWindow size={18} />}>
            <TopPagesTable
              isLoading={isTopPagesLoading}
              topPages={mainRangeResults}
            />
          </Card>
        </div>
      </div>
    </AnalyticsLayout>
  );
};
